import { generatePlaceholderImage } from "./generatePlaceholderImage";
import PropertyModal from "./PropertyModal";
import { useState, useEffect, useRef } from 'react';
import { PlusCircle, Edit, Trash2, Search, X } from 'lucide-react';
import axios from 'axios'
import BlogAdmin from "./blogAdminPanel.js";

import { showErrorToast, ToastProvider, showSuccessToast } from "./toast";
import 'react-toastify/dist/ReactToastify.css';
const apiPath = `${process.env.REACT_APP_API_URL}api/Properties/search`;

const getFormattedDate = (dateString) => {
  if (!dateString || dateString === "0001-01-01T00:00:00") return "Not Available";
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const amenityOptions = [

  {
    amenityType: 1,
    amenityTypeDescription: 'No Smoking',
    amenityTypeDisplay: 'No Smoking'
  },
  {
    amenityType: 2,
    amenityTypeDescription: 'Washer & Dryer',
    amenityTypeDisplay: 'Washer & Dryer'
  },
  {
    amenityType: 3,
    amenityTypeDescription: 'Dishwasher',
    amenityTypeDisplay: 'Dishwasher'
  },
  {
    amenityType: 4,
    amenityTypeDescription: 'Fenced Yard',
    amenityTypeDisplay: 'Fenced Yard'
  },
  {
    amenityType: 5,
    amenityTypeDescription: 'Pet Friendly',
    amenityTypeDisplay: 'Pet Friendly'
  },
  {
    amenityType: 6,
    amenityTypeDescription: 'Peloton Available',
    amenityTypeDisplay: 'Peloton Available'
  },
  {
    amenityType: 7,
    amenityTypeDescription: 'Laundry in the building',
    amenityTypeDisplay: 'Laundry in the building'
  },
];

const getRentalTypeText = (type) => {
  switch (type) {
    case 'F': return 'Furnished';
    case 'U': return 'Unfurnished';
    case 'SF': return 'Semi-Furnished';
    default: return type;
  }
};

const AdminPanel = () => {
  const [properties, setProperties] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentProperty, setCurrentProperty] = useState(null);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);

  // Upload state management
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [propertiesPerPage] = useState(8);

  // Filter state
  const [filters, setFilters] = useState({});
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [thumbnailsCache, setThumbnailsCache] = useState({});

  // Use a ref to track the current page
  const currentPageRef = useRef(1);
  const mountFirstRef = useRef(true);
  const didMountRef = useRef(false);
  const [thumbNailPreview, setThumbnailPreview] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('properties');

  /**
 * Improved sequential image upload function with better error handling
 */


  const uploadImagesSequentially = async (propertyId, images, onProgress = null) => {
    const uploadResults = [];
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    console.log(`Starting batch upload of ${images.length} images for property ${propertyId}`);

    const validImages = images.filter(image => image.thumbnail?.file);

    if (validImages.length === 0) {
      console.log("No new images to upload.");
      return [];
    }

    let consecutiveFailures = 0;
    const maxConsecutiveFailures = 3;

    try {
      if (consecutiveFailures >= maxConsecutiveFailures) {
        console.error('Too many consecutive failures. Stopping upload process.');
        showErrorToast('Server appears to be having issues. Please try again later.');
        return [];
      }

      if (onProgress) {
        onProgress(0, validImages.length, `Uploading ${validImages.length} images...`);
      }

      const formData = new FormData();
      validImages.forEach((image, index) => {
        formData.append('files', image.thumbnail.file);       // matches Swagger param
        formData.append('displayOrders', index);              // matches Swagger param
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}api/Properties/${propertyId}/images/multiple`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          timeout: 90000,
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percent, 100, `Uploading: ${percent}%`);
            }
          }
        }
      );

      // Simulate per-image success structure for compatibility
      validImages.forEach((image, index) => {
        uploadResults.push({
          index,
          success: true,
          data: response.data,
          originalImage: image
        });
      });

      console.log(`✅ All ${validImages.length} images uploaded successfully`);
      await new Promise(resolve => setTimeout(resolve, 1500)); // delay like original

    } catch (error) {
      console.error(`❌ Batch upload failed:`, error);
      consecutiveFailures++;

      let shouldRetry = false;
      let retryDelay = 2000;

      if (error.code === 'ECONNABORTED') {
        console.error('Upload timeout - file might be too large');
        retryDelay = 5000;
        shouldRetry = true;
      } else if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
        console.error('CORS error - server configuration issue');
        retryDelay = 3000;
        shouldRetry = true;
      } else if (error.response?.status === 401) {
        console.error('Authentication failed');
        throw new Error('Authentication failed. Please log in again.');
      } else if ([502, 503].includes(error.response?.status)) {
        console.error('Server error - will retry after longer delay');
        retryDelay = 5000;
        shouldRetry = true;
      } else if (error.response?.status >= 500) {
        console.error('Server error - will retry');
        retryDelay = 3000;
        shouldRetry = true;
      }

      if (shouldRetry && consecutiveFailures <= 2) {
        try {
          console.log(`Waiting ${retryDelay}ms before retry...`);
          if (onProgress) {
            onProgress(0, validImages.length, `Retrying batch upload after error...`);
          }

          await new Promise(resolve => setTimeout(resolve, retryDelay));

          const retryFormData = new FormData();
          validImages.forEach((image, index) => {
            retryFormData.append('files', image.thumbnail.file);
            retryFormData.append('displayOrders', index);
          });

          const retryResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}api/Properties/${propertyId}/images/multiple`,
            retryFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              timeout: 120000,
              withCredentials: true
            }
          );

          validImages.forEach((image, index) => {
            uploadResults.push({
              index,
              success: true,
              data: retryResponse.data,
              originalImage: image,
              retried: true
            });
          });

          console.log(`✅ Batch uploaded successfully on retry`);
          consecutiveFailures = 0;

        } catch (retryError) {
          console.error(`❌ Retry failed for batch upload:`, retryError);
          validImages.forEach((image, index) => {
            uploadResults.push({
              index,
              success: false,
              error: retryError.message,
              originalImage: image,
              retriedAndFailed: true
            });
          });
        }

      } else {
        validImages.forEach((image, index) => {
          uploadResults.push({
            index,
            success: false,
            error: error.message,
            originalImage: image
          });
        });
      }

      await new Promise(resolve => setTimeout(resolve, 2000)); // like original
    }

    const successCount = uploadResults.filter(r => r.success).length;
    const failCount = uploadResults.filter(r => !r.success).length;

    console.log(`Upload completed: ${successCount} successful, ${failCount} failed`);

    return uploadResults;
  };


  /**
   * Upload thumbnail image
   */
  const uploadThumbnail = async (propertyId, thumbnailFile) => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    if (!thumbnailFile) {
      throw new Error("No thumbnail file provided");
    }

    try {
      console.log('Uploading thumbnail image...');

      const formData = new FormData();
      formData.append('file', thumbnailFile);
      formData.append('displayOrder', 0);
      formData.append('isThumbnail', true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}api/Properties/${propertyId}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
          },
          timeout: 60000,
          withCredentials: true,
        }
      );

      console.log('✅ Thumbnail uploaded successfully');
      return response.data;

    } catch (error) {
      console.error('❌ Thumbnail upload failed:', error);

      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.message.includes('CORS')) {
        throw new Error('CORS error: Please contact administrator to configure API access.');
      } else if (error.response?.status === 502) {
        throw new Error('Server error: Please try again in a moment.');
      }

      throw error;
    }
  };

  const fetchProperties = async (pageNumber = 1, isInitialLoad = false) => {
    try {
      const payload = {
        location: searchQuery || "",
        ...(filters?.available === true ? { available: true } :
          filters?.available === false ? { available: false } : {}),
        ...(filters?.beds > 0 ? {
          minBeds: filters.beds,
          maxBeds: filters.beds
        } : {}),
        ...(filters?.baths > 0 ? {
          minBaths: filters.baths,
          maxBaths: filters.baths
        } : {}),
        ...(filters?.rentalType !== "" ? { rentalType: Number(filters.rentalType) } : {}),
        ...(filters?.parkingType ? { parkingType: filters.parkingType } : {}),
        page: pageNumber,
        pageSize: propertiesPerPage,
        sortBy: "availableDt",
        sortOrder: "asc"
      }

      const res = await axios.post(apiPath, payload);
      const data = res.data.properties;
      const allPropertyIds = data.map((property) => property.id)
      getAllThumbnails(allPropertyIds)
      setTotalProperties(res.data.totalCount)
      setProperties(data);

    } catch (error) {
      showErrorToast("Failed to fetch properties");
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  };

  const getAllThumbnails = async (propertyIds) => {
    const token = localStorage.getItem("token");
    if (!token || !propertyIds?.length) return;

    const requests = propertyIds.map(id => {
      const propertyUrl = `${process.env.REACT_APP_API_URL}api/Properties/${id}`;

      return axios.get(propertyUrl, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(async res => {
          const property = res.data;
          const thumbnailImage = property.images?.find(img => img.isThumbnail);

          if (!thumbnailImage) {
            return { id, thumbnailUrl: null };
          }

          const imageUrl = `${process.env.REACT_APP_API_URL}${thumbnailImage.imageUrl.replace(/^\/+/, '')}`;

          return {
            id,
            thumbnailUrl: imageUrl,
          };
        })
        .catch(error => {
          console.error(`Error loading thumbnail for property ${id}:`, error);
          return { id, thumbnailUrl: null };
        });
    });

    const results = await Promise.allSettled(requests);

    const updatedCache = {};
    results.forEach(result => {
      if (result.status === 'fulfilled') {
        const { id, thumbnailUrl } = result.value;
        updatedCache[id] = thumbnailUrl;
      }
    });

    setThumbnailsCache(prev => ({ ...prev, ...updatedCache }));
  };

  const createProperty = async (propertyInfo) => {
    const createPropertyPath = `${process.env.REACT_APP_API_URL}api/Properties`;
    const token = localStorage.getItem("token");

    try {
      const res = await axios.post(createPropertyPath, propertyInfo, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return res.data;

    } catch (error) {
      showErrorToast("Error creating property");
      console.error("Error creating property:", error);
      if (error.response && error.response.status === 401) {
        showErrorToast("Unauthorized: Please log in again.");
      }
      throw error;
    }
  };

  const editProperty = async (propertyId, propertyInfo) => {
    const editPropertyPath = `${process.env.REACT_APP_API_URL}api/Properties/${propertyId}`;
    const token = localStorage.getItem("token");

    try {
      const res = await axios.put(editPropertyPath, propertyInfo, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return res.data;

    } catch (error) {
      console.error("Error editing property:", error);
      showErrorToast("Error editing property");
      if (error.response && error.response.status === 401) {
        showErrorToast("Unauthorized: Please log in again.");
      }
      throw error;
    }
  };

  // Initialize with the sample data
  useEffect(() => {
    if (!mountFirstRef.current) {
      currentPageRef.current = 1;
      setProperties([]);
      setHasMore(true);
      setInitialLoading(true);
      fetchProperties(1, true);
      setCurrentPage(1);
    } else {
      mountFirstRef.current = false;
    }
  }, [filters.baths, filters.beds, filters.available, filters.rentalType, filters.parkingType]);

  useEffect(() => {
    if (didMountRef.current) {
      const timer = setTimeout(() => {
        setProperties([]);
        setHasMore(true);
        setInitialLoading(true);
        fetchProperties(1, true);
        setCurrentPage(1)
        currentPageRef.current = 1;
      }, 500);

      return () => clearTimeout(timer);
    } else {
      didMountRef.current = true;
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchProperties(currentPage)
  }, [currentPage])

  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "available") {
      setFilters({
        ...filters,
        [name]: value === "true" ? true : value === "false" ? false : null,
      });
    } else if (name === "rentalType") {
      setFilters({
        ...filters,
        rentalType: value,
      });
    } else {
      setFilters({
        ...filters,
        [name]: Number(value),
      });
    }
  };

  const resetFilters = () => {
    setFilters({
      beds: 0,
      baths: 0,
      available: 0,
      rentalType: "",
      parkingType: 0
    });
    setSearchQuery('');
  };

  /**
   * Updated handleCreateProperty with sequential uploads
   */
  const handleCreateProperty = async (newProperty) => {
    setIsUploading(true);
    setUploadProgress('Creating property...');

    try {
      console.log('Starting property creation process...');

      // 1. Create property without images
      const { thumbnailImageUrl, images, ...otherPropertyInfo } = newProperty;
      const createPropertyInput = otherPropertyInfo;
      const createdProperty = await createProperty(createPropertyInput);

      console.log(`✅ Property created with ID: ${createdProperty.id}`);

      let uploadSummary = { thumbnail: null, images: { success: 0, failed: 0 } };

      // 2. Upload thumbnail if provided
      if (thumbnailImageUrl instanceof File) {
        try {
          setUploadProgress('Uploading thumbnail...');
          uploadSummary.thumbnail = await uploadThumbnail(createdProperty.id, thumbnailImageUrl);
          // Add 1-second delay before refresh to ensure DB sync
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Update cache with new thumbnail
          setThumbnailsCache(prev => ({
            ...prev,
            [createdProperty.id]: `${process.env.REACT_APP_API_URL}${uploadSummary.thumbnail.imageUrl}`
          }));

          console.log('✅ Thumbnail uploaded successfully');
        } catch (error) {
          console.error('❌ Thumbnail upload failed:', error);
          showErrorToast(`Thumbnail upload failed: ${error.message}`);
        }
      }

      // 3. Upload additional images sequentially
      const imagesToUpload = images?.filter(img =>
        img.thumbnail?.file && !img.isThumbnail
      );

      if (imagesToUpload?.length > 0) {
        console.log(`📤 Starting upload of ${imagesToUpload?.length} additional images...`);

        const uploadResults = await uploadImagesSequentially(
          createdProperty.id,
          imagesToUpload,
          (current, total, message) => {
            setUploadProgress(`${message}`);
          }
        );

        uploadSummary.images.success = uploadResults.filter(r => r.success).length;
        uploadSummary.images.failed = uploadResults.filter(r => !r.success).length;

        console.log(`📊 Upload summary: ${uploadSummary.images.success} successful, ${uploadSummary.images.failed} failed`);

        if (uploadSummary.images.failed > 0) {
          const failedImages = uploadResults
            .filter(r => !r.success)
            .map(r => `Image ${r.index + 1}`)
            .join(', ');

          showErrorToast(`Some images failed to upload: ${failedImages}`);
        }
      }

      // 4. Show success and cleanup
      setUploadProgress('Complete!');

      setTimeout(async () => {
        setUploadProgress(null);
        setIsUploading(false);
        setIsCreateModalOpen(false);

        // Refresh properties list
        await fetchProperties(currentPage);

        const successMessage = `Property created successfully!${uploadSummary.images.success > 0 ? ` ${uploadSummary.images.success} images uploaded.` : ''}`;
        showSuccessToast(successMessage);
      }, 1500);

    } catch (error) {
      console.error('❌ Create operation failed:', error);
      setIsUploading(false);
      setUploadProgress(null);

      let errorMessage = 'Failed to create property. ';

      if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.message.includes('CORS')) {
        errorMessage += 'Server configuration error. Please contact support.';
      } else if (error.response?.status === 502) {
        errorMessage += 'Server is temporarily unavailable. Please try again.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }

      showErrorToast(errorMessage);
    }
  };

  /**
   * Updated handleEditProperty with sequential uploads
   */
  const handleEditProperty = async (updatedProperty) => {
    setIsUploading(true);
    setUploadProgress('Updating property...');

    try {
      console.log('Starting property update process...');

      // 1. Update property without images
      const { thumbnailImageUrl, images, amenities, ...otherPropertyInfo } = updatedProperty;
      const updatedPropertyInput = otherPropertyInfo;
      const updatedPropertyInfo = await editProperty(updatedProperty.id, updatedPropertyInput);

      console.log(`✅ Property ${updatedProperty.id} updated`);

      let uploadSummary = { thumbnail: null, images: { success: 0, failed: 0 } };

      // 2. Upload thumbnail if it's a new file
      if (thumbnailImageUrl instanceof File) {
        try {
          setUploadProgress('Uploading thumbnail...');
          uploadSummary.thumbnail = await uploadThumbnail(updatedProperty.id, thumbnailImageUrl);
          // Add 1-second delay before refresh to ensure DB sync
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Update cache with new thumbnail
          setThumbnailsCache(prev => ({
            ...prev,
            [updatedProperty.id]: `${process.env.REACT_APP_API_URL}${uploadSummary.thumbnail.imageUrl}`
          }));

          console.log('✅ Thumbnail uploaded successfully');
        } catch (error) {
          console.error('❌ Thumbnail upload failed:', error);
          showErrorToast(`Thumbnail upload failed: ${error.message}`);
        }
      }

      // 3. Upload additional images sequentially
      const imagesToUpload = images.filter(img =>
        img.thumbnail?.file && !img.isThumbnail
      );

      if (imagesToUpload.length > 0) {
        console.log(`📤 Starting upload of ${imagesToUpload.length} additional images...`);

        const uploadResults = await uploadImagesSequentially(
          updatedProperty.id,
          imagesToUpload,
          (current, total, message) => {
            setUploadProgress(`${message}`);
          }
        );

        uploadSummary.images.success = uploadResults.filter(r => r.success).length;
        uploadSummary.images.failed = uploadResults.filter(r => !r.success).length;

        console.log(`📊 Upload summary: ${uploadSummary.images.success} successful, ${uploadSummary.images.failed} failed`);

        if (uploadSummary.images.failed > 0) {
          const failedImages = uploadResults
            .filter(r => !r.success)
            .map(r => `Image ${r.index + 1}`)
            .join(', ');

          showErrorToast(`Some images failed to upload: ${failedImages}`);
        }
      }

      // 4. Show success and cleanup
      setUploadProgress('Complete!');

      setTimeout(async () => {
        setUploadProgress(null);
        setIsUploading(false);
        setIsEditModalOpen(false);
        setCurrentProperty(null);
        setThumbnailPreview(null);

        // Refresh properties list
        await fetchProperties(currentPage);

        const successMessage = `Property updated successfully!${uploadSummary.images.success > 0 ? ` ${uploadSummary.images.success} images uploaded.` : ''}`;
        showSuccessToast(successMessage);
      }, 1500);

    } catch (error) {
      console.error('❌ Update operation failed:', error);
      setIsUploading(false);
      setUploadProgress(null);

      let errorMessage = 'Failed to update property. ';

      if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.message.includes('CORS')) {
        errorMessage += 'Server configuration error. Please contact support.';
      } else if (error.response?.status === 502) {
        errorMessage += 'Server is temporarily unavailable. Please try again.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }

      showErrorToast(errorMessage);
    }
  };

  const deleteProertyById = async (id) => {
    const propertyInfoByIdApiPath = `${process.env.REACT_APP_API_URL}api/Properties/${id}`
    const token = localStorage.getItem("token");
    try {
      const res = await axios.delete(propertyInfoByIdApiPath, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      showSuccessToast("Property deleted successfully")
      return res.data;
    } catch (error) {
      showErrorToast("Error deleting property");
      console.error("Error deleting property:", error.response?.data || error.message);
      if (error.response?.status === 401) {
        showErrorToast("Unauthorized: Please log in again.");
      }
    }
  }

  const handleDeleteProperty = async (id) => {
    try {
      if (window.confirm('Are you sure you want to delete this property?')) {
        await deleteProertyById(id)
        setProperties(properties.filter(p => p.id !== id));
        await fetchProperties(currentPage)
      } else {
        return
      }

    } catch (err) {
      console.log(err)
    }
  };

  const getPropertyInfoById = async (id) => {
    const propertyInfoByIdApiPath = `${process.env.REACT_APP_API_URL}api/Properties/${id}`
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(propertyInfoByIdApiPath, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return res.data;
    } catch (error) {
      console.error("Failed to get property information", error.response?.data || error.message);
      if (error.response?.status === 401) {
        showErrorToast("Unauthorized: Please log in again.");
      }
    }
  }

  const openEditModal = async (property) => {
    const propertyInfo = await getPropertyInfoById(property.id)
    const isThumbNailPresent = propertyInfo?.images?.find((property) => property.isThumbnail === true)

    if (isThumbNailPresent) {
      setThumbnailPreview(`${process.env.REACT_APP_API_URL}${isThumbNailPresent?.imageUrl}`)
    }

    setCurrentProperty(propertyInfo);
    setIsEditModalOpen(true);
  };

  return (
    <div className="container py-4">
      <ToastProvider />
      {/* Tab Navigation */}
      <div className="mb-4">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'properties' ? 'active' : ''}`}
              onClick={() => setActiveTab('properties')}
              disabled={isUploading}
            >
              Property Management
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'blogs' ? 'active' : ''}`}
              onClick={() => setActiveTab('blogs')}
              disabled={isUploading}
            >
              Blog Management
            </button>
          </li>
        </ul>
      </div>
      {activeTab === 'properties' && (<>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 fw-bold text-dark">Property Management</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn btn-primary d-flex align-items-center"
          disabled={isUploading}
        >
          <PlusCircle size={20} className="me-2" />
          Create Property
        </button>
      </div>

      {/* Upload Progress Indicator */}
      {isUploading && (
        <div className="alert alert-info d-flex align-items-center mb-4">
          <div className="spinner-border spinner-border-sm me-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>
            <strong>Processing...</strong>
            {uploadProgress && <div className="small">{uploadProgress}</div>}
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="mb-4">
        <div className="d-flex gap-2 mb-3">
          <div className="position-relative flex-grow-1">
            <div className="position-absolute top-50 start-0 translate-middle-y ms-3">
              <Search size={20} className="text-secondary" />
            </div>
            <input
              type="text"
              className="form-control ps-5"
              placeholder="Search properties by address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isUploading}
            />
            {searchQuery && (
              <button
                className="btn position-absolute top-50 end-0 translate-middle-y me-2"
                onClick={() => setSearchQuery('')}
                disabled={isUploading}
              >
                <X size={20} className="text-secondary" />
              </button>
            )}
          </div>
          <button
            onClick={() => setIsFilterVisible(!isFilterVisible)}
            className="btn btn-outline-secondary"
            disabled={isUploading}
          >
            {isFilterVisible ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {/* Filter Panel */}
        {isFilterVisible && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4 col-lg-2">
                  <label className="form-label">Beds</label>
                  <select
                    name="beds"
                    value={filters.beds || 0}
                    onChange={handleFilterChange}
                    className="form-select"
                    disabled={isUploading}
                  >
                    <option value="0">Any</option>
                    <option value="1">1 bed</option>
                    <option value="2">2 beds</option>
                    <option value="3">3 beds</option>
                  </select>
                </div>

                <div className="col-md-4 col-lg-2">
                  <label className="form-label">Baths</label>
                  <select
                    name="baths"
                    value={filters.baths || 0}
                    onChange={handleFilterChange}
                    className="form-select"
                    disabled={isUploading}
                  >
                    <option value="0">Any</option>
                    <option value="1">1 bath</option>
                    <option value="2">2 baths</option>
                    <option value="3">3 baths</option>
                  </select>
                </div>

                <div className="col-md-4 col-lg-2">
                  <label className="form-label">Availability</label>
                  <select
                    name="available"
                    value={filters.available || "0"}
                    onChange={handleFilterChange}
                    className="form-select"
                    disabled={isUploading}
                  >
                    <option value="0">All</option>
                    <option value="true">Available</option>
                    <option value="false">Not Available</option>
                  </select>
                </div>

                <div className="col-md-4 col-lg-2">
                  <label className="form-label">Parking</label>
                  <select
                    name="parkingType"
                    value={filters.parkingType || 0}
                    onChange={handleFilterChange}
                    className="form-select"
                    disabled={isUploading}
                  >
                    <option value="0">No Parking</option>
                    <option value="1">On-Street Parking</option>
                    <option value="2">Reserved Spot</option>
                  </select>
                </div>

                <div className="col-md-4 col-lg-2">
                  <label className="form-label">Type</label>
                  <select
                    name="rentalType"
                    value={filters.rentalType || ""}
                    onChange={handleFilterChange}
                    className="form-select text-dark"
                    disabled={isUploading}
                  >
                    <option value="" className="text-dark">All</option>
                    <option value="1" className="text-dark">Furnished</option>
                    <option value="0" className="text-dark">Unfurnished</option>
                    <option value="2" className="text-dark">Semi-Furnished</option>
                  </select>
                </div>
              </div>

              <div className="mt-3 text-end">
                <button
                  onClick={resetFilters}
                  className="btn btn-outline-secondary"
                  disabled={isUploading}
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <p className="text-secondary mb-0">
            Showing {totalProperties === 0 ? 0 : indexOfFirstProperty + 1}
            -
            {Math.min(indexOfLastProperty, totalProperties)} of {totalProperties} properties
          </p>
        </div>
      </div>

      {/* Property Cards Grid */}
      <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-4">
        {properties.map(property => (
          <div key={property.id} className="col">
            <div className="card h-100 shadow-sm">
              <div className="position-relative" style={{ height: "200px" }}>
                {property?.thumbnailImageUrl ? (
                  <img
                    src={thumbnailsCache[property.id]}
                    alt={`${property.address} ${property.unit}`}
                    className="card-img-top h-100 object-fit-cover"
                  />
                ) : generatePlaceholderImage(`${property?.address} ${property?.unit}`)}

                <div className="position-absolute top-0 end-0 m-2 d-flex gap-2">
                  <button
                    onClick={() => openEditModal(property)}
                    className="btn btn-sm btn-light rounded-circle"
                    disabled={isUploading}
                  >
                    <Edit size={16} className="text-primary" />
                  </button>
                  <button
                    onClick={() => handleDeleteProperty(property?.id)}
                    className="btn btn-sm btn-light rounded-circle"
                    disabled={isUploading}
                  >
                    <Trash2 size={16} className="text-danger" />
                  </button>
                </div>

                <div className="position-absolute bottom-0 start-0 end-0 p-3 bg-dark bg-opacity-50">
                  <h5 className="card-title text-white text-truncate mb-1">
                    {property?.address}, Unit {property?.unit}
                  </h5>
                  <p className="card-text text-white text-truncate mb-0">
                    {property?.city}, {property?.state}
                  </p>
                </div>
              </div>

              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <span className="h4 fw-bold text-dark mb-0">${property?.price}</span>
                  <span className={`badge ${property?.available ? 'bg-success' : 'bg-danger'}`}>
                    {/* {property?.available ? 'Available' : 'Not Available'} */}
                    {((property.availableDt!=="0001-01-01T00:00:00") && property?.available && (property?.price > 0)) ? 'Available' : 'Not Available'}
                  </span>
                </div>

                <div className="d-flex gap-3 mb-3 text-secondary">
                  <div>
                    <span className="fw-medium">{property.beds}</span>
                    <span className="ms-1">bed{property.beds !== 1 ? 's' : ''}</span>
                  </div>
                  <div>
                    <span className="fw-medium">{property.baths}</span>
                    <span className="ms-1">bath{property.baths !== 1 ? 's' : ''}</span>
                  </div>
                  <div>
                    <span className="fw-medium">{property.sqft}</span>
                    <span className="ms-1">sqft</span>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex text-secondary small mb-1">
                    <span className="fw-medium me-2">Type:</span>
                    {getRentalTypeText(property.rentalTypeDisplay)}
                  </div>
                  <div className="d-flex text-secondary small mb-1">
                    <span className="fw-medium me-2">Availability Date:</span>
                    {getFormattedDate(property.availableDt)}
                  </div>
                  <div className="d-flex text-secondary small">
                    <span className="fw-medium me-2">Min Term:</span>
                    {property.minTermMos} month{property.minTermMos !== 1 ? 's' : ''}
                  </div>
                </div>

                <div className="mb-3">
                  <p className="card-text small text-secondary" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {property.description}
                  </p>
                </div>

                {property.amenityTypes && property.amenityTypes.length > 0 && (
                  <div className="d-flex flex-wrap gap-2">
                    {property.amenityTypes.map((type, index) => {
                      const amenity = amenityOptions.find(opt => opt.amenityType === type);
                      return (
                        <span key={index} className="badge bg-primary bg-opacity-10 text-white">
                          {amenity ? amenity.amenityTypeDisplay : type}
                        </span>
                      );
                    })}

                    {property.utilities && (
                      <span className="badge bg-success bg-opacity-10 text-white">
                        Utilities Included
                      </span>
                    )}
                    {property.internet && (
                      <span className="badge bg-info bg-opacity-10 text-white">
                        Internet Included
                      </span>
                    )}
                    {property.parkingTypeDisplay && (
                      <span className="badge bg-info bg-opacity-10 text-white">
                        {property.parkingTypeDisplay}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalProperties === 0 && (
        <div className="text-center py-5">
          <p className="text-secondary fs-5">No properties found. Try adjusting your search or filters.</p>
        </div>
      )}

      {/* Pagination */}
      {totalProperties > 0 && (
        <nav className="d-flex justify-content-center mt-4">
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                disabled={currentPage === 1 || isUploading}
              >
                Prev
              </button>
            </li>

            {Array.from({ length: Math.ceil(totalProperties / propertiesPerPage) }).map((_, index) => {
              const pageNumber = index + 1;
              const isCurrentPage = pageNumber === currentPage;

              const shouldShowPage =
                pageNumber === 1 ||
                pageNumber === Math.ceil(totalProperties / propertiesPerPage) ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

              if (shouldShowPage) {
                return (
                  <li key={pageNumber} className={`page-item ${isCurrentPage ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => paginate(pageNumber)}
                      disabled={isUploading}
                    >
                      {pageNumber}
                    </button>
                  </li>
                );
              }

              return null;
            })}

            <li className={`page-item ${currentPage === Math.ceil(totalProperties / propertiesPerPage) ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => paginate(
                  currentPage < Math.ceil(totalProperties / propertiesPerPage)
                    ? currentPage + 1
                    : Math.ceil(totalProperties / propertiesPerPage)
                )}
                disabled={currentPage === Math.ceil(totalProperties / propertiesPerPage) || isUploading}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      {/* Create Property Modal */}
      {isCreateModalOpen && (
        <PropertyModal
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateProperty}
          isCreate={true}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
        />
      )}

      {/* Edit Property Modal */}
      {isEditModalOpen && currentProperty && (
        <PropertyModal
          onClose={() => {
            setIsEditModalOpen(false);
            setCurrentProperty(null);
            setThumbnailPreview(null);
          }}
          thumbNailImage={thumbNailPreview}
          onSave={handleEditProperty}
          property={currentProperty}
          isCreate={false}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
        />
      )}
    </>)}
     {activeTab === 'blogs' && (
      <>
      <BlogAdmin/>
     </>
    )}
    </div>
  );
};

export default AdminPanel;